/**
 * The administration module's paths — spec §8.11.
 *
 * `/admin` is what `layout/nav-items.ts` already points its `admin` entry at, gated on
 * `users:read | settings:write | apikeys:manage | audit:read`, so the prefix is not
 * this phase's to choose. Everything below hangs off it.
 *
 * ─── `/admin` IS AN INDEX, NOT A REDIRECT ───────────────────────────────────
 *
 * The seven sections below are gated on SEVEN DIFFERENT permissions, and a tenant_admin
 * is the only role that holds all of them. A dispatcher holds `devices:command` and
 * nothing else here; a fleet manager holds the three import permissions and no more.
 * Redirecting `/admin` to a fixed first section would land most of those users on the
 * forbidden state of a screen they never asked for, so `/admin` renders the sections
 * the caller can actually open and says plainly that the rest exist and are withheld.
 *
 * ─── THE DEVICE CONSOLE CARRIES ITS DEVICE ON THE PATH ──────────────────────
 *
 * `/admin/devices/:deviceId`, not `/admin/devices?device=…`. An installer standing at a
 * vehicle sends a colleague the link to the unit they are arguing about, and a path
 * segment survives the copy-paste that strips a query string. It is also the address a
 * support engineer pastes into an incident report six months later, beside the audit
 * row that names the same device id.
 *
 * ─── THE WEBHOOK DELIVERY LOG CARRIES ITS FILTER ON THE QUERY ───────────────
 *
 * `?status=dead&from=…&to=…` IS the missed-events view rule 2 asks for, and it has to
 * be a link a customer can be SENT. "Open webhooks, choose dead, set the range to
 * Tuesday" is a support instruction; a URL is an answer.
 *
 * Route paths stay in English while the UI defaults to French: the address bar is not a
 * translated surface, and a localized set would mean two routing tables and a redirect
 * on every language switch.
 *
 * Registration lives in `routes/app-routes.tsx`, which this phase does not own.
 * NOTES-PHASE10-ADMIN.md §1 carries the patch, and it MUST lazy-load this module's
 * barrel — importing it registers a 400-key FR/EN catalogue as a side effect.
 */

export const ADMIN_PATH = '/admin'
export const ADMIN_DEVICES_PATH = '/admin/devices'
export const ADMIN_DEVICE_CONSOLE_PATH = '/admin/devices/:deviceId'
export const ADMIN_IMPORT_PATH = '/admin/import'
export const ADMIN_BRANDING_PATH = '/admin/branding'
export const ADMIN_API_KEYS_PATH = '/admin/api-keys'
export const ADMIN_WEBHOOKS_PATH = '/admin/webhooks'
export const ADMIN_AUDIT_PATH = '/admin/audit'
export const ADMIN_USERS_PATH = '/admin/users'
export const ADMIN_WORKING_HOURS_PATH = '/admin/working-hours'

export function deviceConsolePath(deviceId: string): string {
  return `${ADMIN_DEVICES_PATH}/${encodeURIComponent(deviceId)}`
}

/**
 * The query keys the audit and delivery screens read.
 *
 * Named constants rather than string literals at each call site: these appear in URLs
 * that customers keep, and a rename that only touched half the readers would produce a
 * link that silently drops the filter it was sent to demonstrate.
 */
export const ADMIN_QUERY_KEYS = {
  status: 'status',
  subscription: 'subscription',
  from: 'from',
  to: 'to',
  action: 'action',
  actor: 'actor',
  resourceType: 'resourceType',
} as const

/** `?status=dead` over the last `days` — the link a customer is sent after an outage. */
export function deadLetterPath(days = 7, now: Date = new Date()): string {
  const from = new Date(now.getTime() - days * 86_400_000)
  const query = new URLSearchParams({
    [ADMIN_QUERY_KEYS.status]: 'dead',
    [ADMIN_QUERY_KEYS.from]: from.toISOString(),
  })
  return `${ADMIN_WEBHOOKS_PATH}?${query.toString()}`
}
